import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import PropTypes from 'prop-types';
import { capitalize } from '../../../utils/formatting';

import { Select } from '../../../components/Form';
import EditMatchScoreGrid from './EditMatchScoreGrid';
import { OUTCOME_OPTIONS, OUTCOMES_TO_ALLOW_SCORING, OUTCOMES_WITH_WINNER_BY_TEAM_OR_PLAYER_SELECTOR, PLAYER_OR_TEAM_SELECTOR } from './consts';
import useGetMatchPost from './useGetMatchPost';
import { ballTypeIds, drawPositionTypeIds, eventFormatTypeIds, sportTypeIds, utrTypeIds } from '../../../utils/eventConstants';
import { areScoresValid, buildPostScorePlayersData, validatePostScoreData } from '../../../utils/postScore';
import { isSinglesFormat } from '../../../utils/events';

const EventDeskPostScore = forwardRef((props, ref) => {
    const {
        eventId,
        match,
        sportTypeId,
        utrTypeId,
        playersDictByMemberId,
        teamsDict,
        clubTierType,
        onScoreChanged,
        onValidateScore,
        'data-testid': dataTestId,
    } = props;

    const {
        isWinner,
        winningTeam,
        losingTeam,
        sets,
        isCompleted,
        matchOutcome,
        handleInputChange,
        handleSwapPositions,
        handleLosingTeamOrPlayerSelected,
        handleOutcomeChange,
        areScoresChanged,
        getWinnerLoserSetData,
        attachRef,
        handleScoreKeyDown,
        setCurrentSet,
    } = useGetMatchPost({ match, playersDictByMemberId, teamsDict, sportTypeId });

    const showScoreSection = OUTCOMES_TO_ALLOW_SCORING.includes(matchOutcome);
    const showTeamOrPlayerSelectionSection = OUTCOMES_WITH_WINNER_BY_TEAM_OR_PLAYER_SELECTOR.includes(matchOutcome);
    const isDoubles = match?.position1?.type?.value === 'team' || match?.position1?.typeId === drawPositionTypeIds.TEAM;
    const isVerifiedEvent = utrTypeId === utrTypeIds.SANCTIONED;

    const getPostScoreData = useCallback(() => buildPostScorePlayersData({
        winnerId: winningTeam,
        loserId: losingTeam,
        isDoubles,
        match,
        teamsDict,
        playersDictByMemberId,
    }), [winningTeam, losingTeam, isDoubles, match, teamsDict, playersDictByMemberId]);

    const getScore = (outcome, winnerLoserSetScore) => {
        if (!OUTCOMES_TO_ALLOW_SCORING.includes(outcome)) {
            return '';
        }

        if (winnerLoserSetScore || winnerLoserSetScore === 0) {
            return winnerLoserSetScore;
        }

        return '';
    };

    const getPostScoreResult = useCallback(() => {
        const postScoreData = getPostScoreData();

        const winnerLoserMatchSetData = getWinnerLoserSetData();

        return {
            eventId,
            date: match.startTime,
            teamType: isSinglesFormat(match?.drawFormatTypeId) ? 'S' : 'D',
            sportTypeId,
            isWinner,
            matchId: match.id,
            winnerSet1: getScore(matchOutcome, winnerLoserMatchSetData?.set1?.winnerSet),
            loserSet1: getScore(matchOutcome, winnerLoserMatchSetData?.set1?.loserSet),
            winnerSet2: getScore(matchOutcome, winnerLoserMatchSetData?.set2?.winnerSet),
            loserSet2: getScore(matchOutcome, winnerLoserMatchSetData?.set2?.loserSet),
            winnerSet3: getScore(matchOutcome, winnerLoserMatchSetData?.set3?.winnerSet),
            loserSet3: getScore(matchOutcome, winnerLoserMatchSetData?.set3?.loserSet),
            tiebreakerSet1: getScore(matchOutcome, winnerLoserMatchSetData?.set1?.tiebreaker),
            tiebreakerSet2: getScore(matchOutcome, winnerLoserMatchSetData?.set2?.tiebreaker),
            tiebreakerSet3: getScore(matchOutcome, winnerLoserMatchSetData?.set3?.tiebreaker),
            resultSourceType: isVerifiedEvent ? 0 : 1,
            completed: isCompleted,
            matchOutcome,
            ...postScoreData,
        };
    }, [getPostScoreData, getWinnerLoserSetData, match, matchOutcome, isVerifiedEvent, isCompleted]);

    const handleSetMatchSets = (event, set, position) => {
        handleInputChange(set, position, event);
    };

    const onSwapSets = () => {
        handleSwapPositions();
    };

    const validatePostScore = useCallback(() => {
        const postScoreData = getPostScoreData();
        return validatePostScoreData({
            sets,
            isDoubles,
            isWinner,
            isCompleted,
            matchOutCome: matchOutcome,
            clubTierType,
            postScoreData,
            isVerifiedResult: isVerifiedEvent,
            isTimeBasedDraw: match?.drawIsTimeBased,
            isColorball: match?.drawBallTypeId !== null && match?.drawBallTypeId > ballTypeIds.YELLOW,
        });
    }, [getPostScoreData, sets, isDoubles, isWinner, isCompleted, matchOutcome, clubTierType, isVerifiedEvent, match]);

    useEffect(() => {
        if (areScoresChanged) {
            onScoreChanged();
        }
    }, [areScoresChanged, onScoreChanged]);

    useEffect(() => {
        onValidateScore(areScoresValid(sets, matchOutcome));
    }, [sets, matchOutcome]);

    const canSubmitPostScoreResult = useCallback(() => {
        return areScoresChanged;
    }, [areScoresChanged]);

    const teamOrPlayerSelectorOptions = useMemo(() => {
        const optionsList = [];

        if (match.participatingMembers) {
            const buildOption = (position) => {
                const memberIds = position?.memberIds ?? (position?.memberId ? [position.memberId] : []);
                const names = memberIds.map((memberId) => {
                    const player = match.participatingMembers.find((m) => m.id === memberId);
                    return player ? `${player.firstName} ${player.lastName}` : 'TBD';
                });
                const displayName = names.join(' / ') || 'TBD';

                const isTeam = position?.typeId === drawPositionTypeIds.TEAM && position?.teamId;
                let id = null;
                if (isTeam) {
                    id = position.teamId;
                } else if (memberIds.length > 0) {
                    id = match.participatingMembers.find((m) => m.id === memberIds[0])?.playerId;
                }

                return { label: displayName, displayName, id };
            };

            optionsList.push(buildOption(match.position1));
            optionsList.push(buildOption(match.position2));
            return optionsList;
        }

        // Legacy path: doubles via teamsDict
        if (match.drawFormatTypeId === eventFormatTypeIds.DOUBLES) {
            const position1Team = teamsDict[match.position1?.teamId];
            const position2Team = teamsDict[match.position2?.teamId];
            optionsList.push({
                label: position1Team?.displayName,
                displayName: position1Team?.displayName,
                id: match.position1?.teamId,
            });
            optionsList.push({
                label: position2Team?.displayName,
                displayName: position2Team?.displayName,
                id: match.position2?.teamId,
            });

            return optionsList;
        }

        // Legacy path: singles via playersDictByMemberId
        const position1Player = playersDictByMemberId[match.position1?.memberId];
        const position2Player = playersDictByMemberId[match.position2?.memberId];
        optionsList.push({
            label: `${position1Player?.firstName} ${position1Player?.lastName}`,
            displayName: `${position1Player?.firstName} ${position1Player?.lastName}`,
            id: position1Player?.playerId,
        });
        optionsList.push({
            label: `${position2Player?.firstName} ${position2Player?.lastName}`,
            displayName: `${position2Player?.firstName} ${position2Player?.lastName}`,
            id: position2Player?.playerId,
        });

        return optionsList;
    }, [match.drawFormatTypeId, match.participatingMembers, playersDictByMemberId, teamsDict]);

    useImperativeHandle(ref, () => ({
        getPostScoreResult,
        validatePostScore,
        canSubmitPostScoreResult,
        areScoreValid: () => {
            return areScoresValid(sets, matchOutcome);
        },
    }));

    const filterOutcomeOptions = ([, value]) => {
        if (value === OUTCOME_OPTIONS.FORFEIT) {
            return sportTypeId === sportTypeIds.PICKLEBALL;
        }

        if (value === OUTCOME_OPTIONS.DEFAULTED) {
            return sportTypeId === sportTypeIds.TENNIS;
        }

        return true;
    };

    return (
        <>
            <Select
                id="matchOutcome"
                options={Object.entries(OUTCOME_OPTIONS).filter(filterOutcomeOptions).map(([, value]) => ({
                    label: capitalize(value),
                    id: value,
                }))}
                emptyOption={'Select outcome'}
                themeV3
                controlled={true}
                value={matchOutcome}
                label="What was the match outcome?"
                onChange={handleOutcomeChange}
                data-testid={dataTestId && `${dataTestId}.outcome-select`}
            />
            {showTeamOrPlayerSelectionSection && (
                <Select
                    id={PLAYER_OR_TEAM_SELECTOR[matchOutcome]?.id}
                    options={teamOrPlayerSelectorOptions}
                    emptyOption={`Select ${PLAYER_OR_TEAM_SELECTOR[matchOutcome]?.label} player/team`}
                    themeV3
                    controlled={true}
                    value={losingTeam}
                    label={`Who ${PLAYER_OR_TEAM_SELECTOR[matchOutcome]?.label}?`}
                    onChange={handleLosingTeamOrPlayerSelected}
                    data-testid={dataTestId && `${dataTestId}.losing-team-select`}
                />
            )}
            {showScoreSection && (
                <EditMatchScoreGrid
                    onSetMatchSets={handleSetMatchSets}
                    sets={sets}
                    handleSwapPositions={onSwapSets}
                    teams={teamOrPlayerSelectorOptions}
                    sportTypeId={sportTypeId}
                    attachRef={attachRef}
                    onKeyDown={handleScoreKeyDown}
                    setCurrentSet={setCurrentSet}
                    data-testid={dataTestId ? `${dataTestId}.score-grid` : 'event-desk.score-grid'}
                />
            )}
        </>
    );
});

EventDeskPostScore.propTypes = {
    utrTypeId: PropTypes.number.isRequired,
    eventId: PropTypes.number.isRequired,
    match: PropTypes.shape({
        id: PropTypes.number,
        startTime: PropTypes.string,
        drawFormatTypeId: PropTypes.number,
        drawIsTimeBased: PropTypes.bool,
        drawBallTypeId: PropTypes.number,
        position1: PropTypes.shape({
            type: PropTypes.shape({ value: PropTypes.string }),
            typeId: PropTypes.number,
            memberId: PropTypes.number,
            teamId: PropTypes.number,
        }),
        position2: PropTypes.shape({
            type: PropTypes.shape({ value: PropTypes.string }),
            typeId: PropTypes.number,
            memberId: PropTypes.number,
            teamId: PropTypes.number,
        }),
        participatingMembers: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number,
            firstName: PropTypes.string,
            lastName: PropTypes.string,
            playerId: PropTypes.number,
        })),
    }).isRequired,
    playersDictByMemberId: PropTypes.objectOf(PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        playerId: PropTypes.number,
    })).isRequired,
    teamsDict: PropTypes.objectOf(PropTypes.shape({
        displayName: PropTypes.string,
    })).isRequired,
    clubTierType: PropTypes.shape({
        name: PropTypes.string,
        isCollege: PropTypes.bool,
        isHighSchool: PropTypes.bool,
    }).isRequired,
    onScoreChanged: PropTypes.func.isRequired,
    onValidateScore: PropTypes.func,
    sportTypeId: PropTypes.number,
    'data-testid': PropTypes.string,
};

EventDeskPostScore.defaultProps = {
    sportTypeId: sportTypeIds.TENNIS,
    onValidateScore: () => {},
    'data-testid': undefined,
};

export default EventDeskPostScore;
